import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { styled } from "@mui/material/styles";
import { useTheme, useMediaQuery } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDispatch, useSelector } from "react-redux";
import { fetchDesignationBased } from "../../actions/DesignationBasedAction";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import homeImage from "../../assets/DTS2.png";

// ------------ Styles ------------
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: {
    padding: "10px 10px",
  },
}));

const StickyCardsContainer = styled(Box)(() => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#ffffff",
  zIndex: 100,
  paddingTop: "10px",
  paddingBottom: "10px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  overflowY: "auto",
  maxHeight: "calc(100vh - 250px)",
  [theme.breakpoints.down("sm")]: {
    maxHeight: "calc(100vh - 280px)",
  },
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "7px 10px",
  fontSize: "14px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    "&:first-of-type": { borderTopLeftRadius: "16px" },
    "&:last-of-type": { borderTopRightRadius: "16px" },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px 5px",
    fontSize: "11px",
    "&.MuiTableCell-head": { fontSize: "11px", letterSpacing: "0px", padding: "6px 5px" },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": { backgroundColor: "rgba(243, 246, 250, 0.6)" },
  "&:hover": { backgroundColor: "rgba(14, 60, 102, 0.09)" },
  "&:last-of-type td:first-of-type": { borderBottomLeftRadius: "16px" },
  "&:last-of-type td:last-of-type": { borderBottomRightRadius: "16px" },
}));

// ------------ Main Component ------------
export default function DesignationBasedEmp() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading, data } = useSelector((state) => state.designationBased);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formatDate = (dateObj) => new Date(dateObj).toISOString().split("T")[0];

  const formatTime = (dateObj) =>
    new Date(dateObj).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  useEffect(() => {
    dispatch(fetchDesignationBased(formatDate(new Date())));
  }, [dispatch]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    dispatch(fetchDesignationBased(formatDate(date)));
  };

  // ✅ Sort by Strength descending only
  const sortedData = data
    ? [...data].sort((a, b) => Number(b.Strength || 0) - Number(a.Strength || 0))
    : [];

  const totalStrength = sortedData.reduce((sum, item) => sum + Number(item.Strength || 0), 0);
  const totalAttendance = sortedData.reduce((sum, item) => sum + Number(item.Attendance || 0), 0);
  const totalPercentage = totalStrength > 0 ? Math.round((totalAttendance / totalStrength) * 100) : 0;

  // ------------ PDF Export ------------
  const handleExportTablePDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.addImage(homeImage, "PNG", 0, 0, 210, 65);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text("Designation Strength & Attendance Report", pageWidth / 2, 75, { align: "center" });

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, 82, pageWidth - 15, 82);

    // Single row — Date | Time | Total Strength | Total Attendance
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    const yPosition = 90;
    const startX = 15;
    const totalWidth = pageWidth - 15 - startX;
    const section1 = startX + totalWidth * 0.125;
    const section2 = startX + totalWidth * 0.375;
    const section3 = startX + totalWidth * 0.625;
    const section4 = startX + totalWidth * 0.875;

    const dateText = "Date - " + formatDate(selectedDate);
    doc.setFont("helvetica", "bold");
    doc.text("Date - ", section1 - doc.getTextWidth(dateText) / 2, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(selectedDate), section1 - doc.getTextWidth(dateText) / 2 + doc.getTextWidth("Date - "), yPosition);

    const timeText = "Time - " + formatTime(generatedAt);
    doc.setFont("helvetica", "bold");
    doc.text("Time - ", section2 - doc.getTextWidth(timeText) / 2, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(formatTime(generatedAt), section2 - doc.getTextWidth(timeText) / 2 + doc.getTextWidth("Time - "), yPosition);

    const strengthText = "Total Strength - " + totalStrength;
    doc.setFont("helvetica", "bold");
    doc.text("Total Strength - ", section3 - doc.getTextWidth(strengthText) / 2, yPosition);
    doc.setFont("helvetica", "normal");
    doc.text(totalStrength.toString(), section3 - doc.getTextWidth(strengthText) / 2 + doc.getTextWidth("Total Strength - "), yPosition);

    const attLabel = "Attendance - ";
    const attValue = `${totalAttendance} (${totalPercentage}%)`;
    const attFull = attLabel + attValue;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);
    doc.text(attLabel, section4 - doc.getTextWidth(attFull) / 2, yPosition);
    if (totalPercentage > 70) doc.setTextColor(27, 94, 32);
    else if (totalPercentage >= 50) doc.setTextColor(230, 81, 0);
    else doc.setTextColor(198, 40, 40);
    doc.text(attValue, section4 - doc.getTextWidth(attFull) / 2 + doc.getTextWidth(attLabel), yPosition);
    doc.setTextColor(60, 60, 60);

    const rows = sortedData
      .filter((item) => item.Designation?.toUpperCase() !== "GENERAL MANAGER")
      .map((item) => [
        item.Designation || "",
        item.Strength || "",
        item.Attendance !== undefined && item.Attendance !== null && item.Attendance !== "" ? item.Attendance : "0",
        `${Math.round(Number(item.Percentage))}%`,
      ]);
    rows.push(["Total", totalStrength.toString(), `${totalAttendance} (${totalPercentage}%)`, `${totalPercentage}%`]);

    autoTable(doc, {
      startY: 97,
      head: [["Designation", "Strength", "Attendance", "Percentage"]],
      body: rows,
      styles: { fontSize: 11, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: {
        fillColor: [21, 76, 121],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 10,
        halign: "center",
        cellPadding: 6,
      },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 75, halign: "left" },
        1: { cellWidth: 35, halign: "center" },
        2: { cellWidth: 35, halign: "center" },
        3: { cellWidth: 40, halign: "center" },
      },
      margin: { left: 10, right: 10 },
      theme: "grid",
      didParseCell: function (data) {
        // Total row styling
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fillColor = [21, 76, 121];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          if (data.column.index === 3 || data.column.index === 2) {
            if (totalPercentage > 70) data.cell.styles.textColor = [144, 238, 144];
            else if (totalPercentage >= 50) data.cell.styles.textColor = [255, 224, 102];
            else data.cell.styles.textColor = [255, 153, 153];
          }
        }
        // Color percentage column for body rows
        if (data.section === "body" && data.row.index < rows.length - 1 && data.column.index === 3) {
          const pct = Math.round(Number(rows[data.row.index]?.[3] || 0));
          if (pct > 70) data.cell.styles.textColor = [27, 94, 32];
          else if (pct >= 50) data.cell.styles.textColor = [230, 81, 0];
          else data.cell.styles.textColor = [198, 40, 40];
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: function (data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(generatedAt)} at ${formatTime(generatedAt)}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pageWidth - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`Designation_Attendance_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#0e3c66",
            textAlign: "center",
            mb: 2,
          }}
        >
          Designation Strength &amp; Attendance
        </Typography>

        {/* Date | Export PDF */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<CalendarTodayIcon />}
            onClick={() => setOpenDatePicker(true)}
            sx={{
              flex: 1,
              height: "56px",
              borderRadius: "20px",
              backgroundColor: "#142c54",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: { xs: "13px", sm: "16px" },
              "&:hover": { backgroundColor: "#0D47A1" },
            }}
          >
            {formatDate(selectedDate)}
          </Button>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportTablePDF}
            disabled={loading || !data?.length}
            sx={{
              flex: 1,
              height: "56px",
              borderRadius: "20px",
              backgroundColor: "#2e7d32",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: { xs: "13px", sm: "16px" },
              "&:hover": { backgroundColor: "#1b5e20" },
              "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
            }}
          >
            Export PDF
          </Button>
        </Box>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
          />
        )}

        {/* Summary Cards */}
        {!loading && data?.length > 0 && (
          <StickyCardsContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: isMobile ? "10px" : "15px",
                px: isMobile ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  background: "#e8f0fa",
                  border: "1px solid #bbd0ea",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#0E3C66",
                  flex: 1,
                  maxWidth: isMobile ? "48%" : "200px",
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, mb: 0.5 }}>
                  Total Strength
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "20px" : "24px", fontWeight: 700 }}>
                  {totalStrength}
                </Typography>
              </Box>

              <Box
                sx={{
                  background: "linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%)",
                  border: "1px solid #80CBC4",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  borderRadius: "8px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#004D40",
                  flex: 1,
                  maxWidth: isMobile ? "48%" : "200px",
                }}
              >
                <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, mb: 0.5 }}>
                  Attendance
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "20px" : "24px", fontWeight: 700 }}>
                  {totalAttendance}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "11px" : "12px", fontWeight: 500, color: "#2E7D32" }}>
                  ({totalPercentage}%)
                </Typography>
              </Box>
            </Box>
          </StickyCardsContainer>
        )}

        {/* Table */}
        <ScrollableContent>
          <StyledTableContainer
            component={Paper}
            sx={{
              maxHeight: "none",
              overflowX: "hidden",
              overflow: "visible",
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ textAlign: "left", width: "34%" }}>Designation</StyledTableCell>
                  <StyledTableCell sx={{ width: "18%" }}>Strength</StyledTableCell>
                  <StyledTableCell sx={{ width: "24%" }}>Attendance</StyledTableCell>
                  <StyledTableCell sx={{ width: "24%" }}>Percentage</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  sortedData
                    .filter((row) => row.Designation?.toUpperCase() !== "GENERAL MANAGER")
                    .map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell sx={{ textAlign: "left", wordBreak: "break-word" }}>
                          {row.Designation}
                        </StyledTableCell>
                        <StyledTableCell>{row.Strength}</StyledTableCell>
                        <StyledTableCell>{row.Attendance}</StyledTableCell>
                        <StyledTableCell>
                          <Typography
                            sx={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "inherit",
                              fontWeight: 700,
                              color:
                                Math.round(Number(row.Percentage)) > 70
                                  ? "#2e7d32"
                                  : Math.round(Number(row.Percentage)) >= 50
                                  ? "#e65100"
                                  : "#c62828",
                            }}
                          >
                            {Math.round(Number(row.Percentage))}%
                          </Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}

                {/* Total Row */}
                {!loading && data?.length > 0 && (
                  <StyledTableRow
                    sx={{
                      backgroundColor: "#154c79 !important",
                      "&:hover": { backgroundColor: "#154c79 !important" },
                    }}
                  >
                    <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700, textAlign: "left" }}>
                      Total
                    </StyledTableCell>
                    <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700 }}>{totalStrength}</StyledTableCell>
                    <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700 }}>{totalAttendance}</StyledTableCell>
                    <StyledTableCell sx={{ color: "#ffffff", fontWeight: 700 }}>{totalPercentage}%</StyledTableCell>
                  </StyledTableRow>
                )}

                {loading && (
                  <TableRow>
                    <StyledTableCell colSpan={4}>Loading...</StyledTableCell>
                  </TableRow>
                )}

                {!loading && (!data || data.length === 0) && (
                  <TableRow>
                    <StyledTableCell colSpan={4}>No Records Found</StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </ScrollableContent>
      </PageContainer>
    </LocalizationProvider>
  );
}